import { Injectable, signal } from '@angular/core';

import { PlatformAuthSuccessDataDto, PlatformAuthUserDto } from '@core/models/platform-auth.dto';

/** Chave legada de access token; mantida para limpeza idempotente de storage antigo. */
export const PLATFORM_ACCESS_TOKEN_SESSION_KEY = 'imo.pat' as const;
/** Chave legada de refresh token; mantida para limpeza idempotente de storage antigo. */
export const PLATFORM_REFRESH_TOKEN_SESSION_KEY = 'imo.prt' as const;
export const PLATFORM_SESSION_STORAGE_KEY = 'imo.pss' as const;
export const PLATFORM_LOGIN_FLASH_MESSAGE_KEY = 'imo.plmf' as const;
export const PLATFORM_RETURN_URL_STORAGE_KEY = 'imo.pru' as const;

type StorageMode = 'local' | 'session';

interface PersistedPlatformSession {
  refreshToken: string;
  refreshTokenExpiresAtUtc: string;
  rememberMe: boolean;
  user: PlatformAuthUserDto;
}

@Injectable({ providedIn: 'root' })
export class PlatformSessionService {
  private readonly _accessToken = signal<string | null>(null);
  private readonly _refreshTokenExpiresAtUtc = signal<string | null>(null);
  private readonly _refreshToken = signal<string | null>(null);
  private readonly _rememberMe = signal(false);
  private readonly _user = signal<PlatformAuthUserDto | null>(null);

  readonly accessToken = this._accessToken.asReadonly();
  readonly rememberMe = this._rememberMe.asReadonly();
  readonly refreshToken = this._refreshToken.asReadonly();
  readonly user = this._user.asReadonly();

  constructor() {
    this.restoreFromStorage();
  }

  get platformRole(): string | null {
    return this._user()?.platformRole ?? null;
  }

  get mustChangePassword(): boolean {
    return this._user()?.mustChangePassword === true;
  }

  clearAccessToken(): void {
    this._accessToken.set(null);
  }

  clearSession(): void {
    this._accessToken.set(null);
    this._refreshTokenExpiresAtUtc.set(null);
    this._refreshToken.set(null);
    this._rememberMe.set(false);
    this._user.set(null);
    this.clearPersistedSession();

    if (typeof sessionStorage !== 'undefined')
      sessionStorage.removeItem(PLATFORM_LOGIN_FLASH_MESSAGE_KEY);
  }

  consumeLoginFlashMessageKey(): string | null {
    if (typeof sessionStorage === 'undefined')
      return null;

    const raw = sessionStorage.getItem(PLATFORM_LOGIN_FLASH_MESSAGE_KEY);
    sessionStorage.removeItem(PLATFORM_LOGIN_FLASH_MESSAGE_KEY);
    if (raw == null || raw.trim() === '')
      return null;

    return raw.trim();
  }

  consumeReturnUrl(): string | null {
    if (typeof sessionStorage === 'undefined')
      return null;

    const rawReturnUrl = sessionStorage.getItem(PLATFORM_RETURN_URL_STORAGE_KEY);
    sessionStorage.removeItem(PLATFORM_RETURN_URL_STORAGE_KEY);
    if (rawReturnUrl == null || rawReturnUrl.trim() === '')
      return null;
    return rawReturnUrl;
  }

  hasPersistedSession(): boolean {
    const refreshToken = this._refreshToken();
    if (refreshToken == null || refreshToken === '')
      return false;

    const expiresAtUtc = this._refreshTokenExpiresAtUtc();
    if (expiresAtUtc == null || expiresAtUtc === '')
      return true;

    const expiresAtTimestamp = Date.parse(expiresAtUtc);
    if (Number.isNaN(expiresAtTimestamp))
      return true;

    return expiresAtTimestamp > Date.now();
  }

  hasRole(requiredRole: 'Admin' | 'Editor'): boolean {
    return this.platformRole === requiredRole;
  }

  hasValidSession(): boolean {
    const token = this._accessToken();
    return token != null && token.length > 0;
  }

  setAccessToken(token: string): void {
    const nextAccessToken = token.trim();
    this._accessToken.set(nextAccessToken === '' ? null : nextAccessToken);
  }

  saveReturnUrl(path: string): void {
    if (typeof sessionStorage === 'undefined')
      return;

    const nextPath = path.trim();
    if (!nextPath.startsWith('/'))
      return;

    if (nextPath === '/entrar')
      return;

    sessionStorage.setItem(PLATFORM_RETURN_URL_STORAGE_KEY, nextPath);
  }

  setAuthenticatedSession(data: PlatformAuthSuccessDataDto, rememberMe: boolean): void {
    const nextAccessToken = data.accessToken.trim();
    const nextRefreshToken = data.refreshToken.trim();
    this._accessToken.set(nextAccessToken === '' ? null : nextAccessToken);
    this._refreshTokenExpiresAtUtc.set(data.refreshTokenExpiresAtUtc ?? null);
    this._refreshToken.set(nextRefreshToken === '' ? null : nextRefreshToken);
    this._rememberMe.set(rememberMe);
    this._user.set(data.user);
    this.persistSession();
  }

  setLoginFlashMessageKey(messageKey: string): void {
    if (typeof sessionStorage === 'undefined')
      return;

    const nextKey = messageKey.trim();
    if (nextKey === '')
      return;

    sessionStorage.setItem(PLATFORM_LOGIN_FLASH_MESSAGE_KEY, nextKey);
  }

  setSessionTokens(accessToken: string, refreshToken: string): void {
    const currentUser = this._user();
    if (currentUser == null)
      return;

    this.setAuthenticatedSession(
      {
        accessToken,
        accessTokenExpiresAtUtc: '',
        refreshToken,
        refreshTokenExpiresAtUtc: this._refreshTokenExpiresAtUtc() ?? '',
        user: currentUser,
      },
      this._rememberMe(),
    );
  }

  private clearPersistedSession(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(PLATFORM_ACCESS_TOKEN_SESSION_KEY);
      localStorage.removeItem(PLATFORM_REFRESH_TOKEN_SESSION_KEY);
      localStorage.removeItem(PLATFORM_SESSION_STORAGE_KEY);
    }

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(PLATFORM_ACCESS_TOKEN_SESSION_KEY);
      sessionStorage.removeItem(PLATFORM_REFRESH_TOKEN_SESSION_KEY);
      sessionStorage.removeItem(PLATFORM_SESSION_STORAGE_KEY);
    }
  }

  private persistSession(): void {
    this.clearPersistedSession();

    const refreshToken = this._refreshToken();
    const refreshTokenExpiresAtUtc = this._refreshTokenExpiresAtUtc();
    const user = this._user();
    if (refreshToken == null || refreshToken === '' || user == null)
      return;

    const payload: PersistedPlatformSession = {
      refreshToken,
      refreshTokenExpiresAtUtc: refreshTokenExpiresAtUtc ?? '',
      rememberMe: this._rememberMe(),
      user,
    };

    const value = JSON.stringify(payload);
    const storage = this._rememberMe() ? localStorage : sessionStorage;
    storage.setItem(PLATFORM_REFRESH_TOKEN_SESSION_KEY, refreshToken);
    storage.setItem(PLATFORM_SESSION_STORAGE_KEY, value);
  }

  private restoreFromStorage(): void {
    const sessionPayload = this.readPersistedSession();
    if (sessionPayload == null)
      return;

    this._accessToken.set(null);
    this._refreshTokenExpiresAtUtc.set(sessionPayload.refreshTokenExpiresAtUtc);
    this._refreshToken.set(sessionPayload.refreshToken);
    this._rememberMe.set(sessionPayload.rememberMe);
    this._user.set(sessionPayload.user);
  }

  private readPersistedSession(): PersistedPlatformSession | null {
    if (typeof sessionStorage === 'undefined' || typeof localStorage === 'undefined')
      return null;

    const localRaw = localStorage.getItem(PLATFORM_SESSION_STORAGE_KEY);
    if (localRaw != null && localRaw !== '')
      return this.parsePersistedSession(localRaw, 'local');

    const sessionRaw = sessionStorage.getItem(PLATFORM_SESSION_STORAGE_KEY);
    if (sessionRaw != null && sessionRaw !== '')
      return this.parsePersistedSession(sessionRaw, 'session');

    return null;
  }

  private parsePersistedSession(raw: string, source: StorageMode): PersistedPlatformSession | null {
    try {
      const parsed = JSON.parse(raw) as Partial<PersistedPlatformSession>;
      if (
        parsed.refreshToken == null ||
        parsed.refreshToken === '' ||
        parsed.user == null
      )
        return null;

      return {
        refreshToken: parsed.refreshToken,
        refreshTokenExpiresAtUtc: parsed.refreshTokenExpiresAtUtc ?? '',
        rememberMe: source === 'local',
        user: parsed.user,
      };
    } catch {
      return null;
    }
  }
}
